import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiService from '../services/api';

const POLL_INTERVAL_MS = 60_000;

type SessionStatus = {
  status: 'online' | 'offline' | 'unknown';
  is_online: boolean;
  assigned_ip?: string | null;
  connected_since?: string | null;
  online_minutes?: number | null;
  upload_mb?: number | null;
  download_mb?: number | null;
  last_update?: string | null;
};

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

function formatBytes(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: '#1e3a5c', px: 2, py: 0.75, mx: -3, mt: -3, mb: 2.5, borderRadius: '4px 4px 0 0' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function StatusDot({ status }: { status: 'online' | 'offline' | 'unknown' }) {
  const color = status === 'online' ? '#1a7f4b' : status === 'unknown' ? '#b45309' : '#64748b';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: color,
        mr: 1,
        flexShrink: 0,
      }}
    />
  );
}

function StatField({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Grid>
  );
}

type Props = {
  pppoeUsername: string;
};

export default function SupaConnectionStatus({ pppoeUsername }: Props) {
  const [data, setData] = useState<SessionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetch = useCallback(async () => {
    try {
      const result = await apiService.adminGetRadiusSessionStatus(pppoeUsername);
      setData(result);
      setError(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'Failed to fetch session status');
    } finally {
      setLoading(false);
      setLastChecked(new Date());
    }
  }, [pppoeUsername]);

  useEffect(() => {
    fetch();
    timerRef.current = setInterval(fetch, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetch]);

  const handleRefresh = () => {
    setLoading(true);
    fetch();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetch, POLL_INTERVAL_MS);
  };

  const statusLabel =
    data?.status === 'online' ? 'Online' :
    data?.status === 'unknown' ? 'Unknown — stale session' :
    'Offline';

  const statusColor =
    data?.status === 'online' ? '#1a7f4b' :
    data?.status === 'unknown' ? '#b45309' :
    '#64748b';

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <SectionLabel>Connection status</SectionLabel>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: data?.is_online ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {loading ? (
            <CircularProgress size={14} sx={{ mr: 1 }} />
          ) : (
            <StatusDot status={data?.status ?? 'offline'} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 700, color: statusColor }}>
            {loading ? 'Checking…' : error ? 'Error' : statusLabel}
          </Typography>
          {error && (
            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
              {error}
            </Typography>
          )}
          {data?.status === 'unknown' && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              Last interim update &gt; 10 min ago — router may have crashed without sending Accounting-Stop
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastChecked && (
            <Typography variant="caption" color="text.secondary">
              Updated {lastChecked.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {data?.is_online && (
        <Grid container spacing={2}>
          {data.assigned_ip && (
            <StatField label="Assigned IP" value={data.assigned_ip} />
          )}
          {data.online_minutes != null && (
            <StatField label="Session duration" value={formatDuration(data.online_minutes)} />
          )}
          {data.connected_since && (
            <StatField
              label="Connected since"
              value={new Date(data.connected_since).toLocaleString('en-AU', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            />
          )}
          {data.upload_mb != null && (
            <StatField label="Upload" value={formatBytes(data.upload_mb)} />
          )}
          {data.download_mb != null && (
            <StatField label="Download" value={formatBytes(data.download_mb)} />
          )}
        </Grid>
      )}
    </Paper>
  );
}
